import React from 'react';
import { intl } from '../../lib';
import { isEmptyArray, isEmptyString } from '../../util';
import { VBox } from '../layout';
import {
  getPatientDetailsNotSpecifiedString,
  PatientDetailsHorizontalFlexElement,
  PatientDetailsPatientProp,
  PatientDetailsSection
} from './internal';

/**
 * Props for a {@link InsurancePlan} component.
 * @author agd
 */
type InsurancePlanProps = {
  insuranceCompanyName: string;
  insuredPersonID: string;
};

/**
 * A single insurance plan displayed in a {@link PatientDetailsInsurancePlans} component.
 *
 * @param props a {@link InsurancePlanProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
function InsurancePlan({
  insuranceCompanyName,
  insuredPersonID
}: InsurancePlanProps): JSX.Element {
  return (
    <VBox className="insurancePlan">
      <PatientDetailsHorizontalFlexElement
        label={intl('view.patient.detail.insuranceCompanyName')}
        value={insuranceCompanyName}
      />
      <PatientDetailsHorizontalFlexElement
        label={intl('view.patient.detail.insuredPersonIDNumber')}
        value={insuredPersonID}
      />
    </VBox>
  );
}

/**
 * Props for a {@link PatientDetailsInsurancePlans} component.
 * @author agd
 */
export type PatientDetailsInsurancePlansProps = PatientDetailsPatientProp;

/**
 * Component to display the insurancePlan details of a patient.
 *
 * @param patient the patient to display the data for
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function PatientDetailsInsurancePlans({
  patient
}: PatientDetailsInsurancePlansProps): JSX.Element {
  const insurancePlans = patient.insurancePlans?.filter(
    plan =>
      !isEmptyString(plan.insuranceCompanyName) &&
      !isEmptyArray(plan.insuredPersonID) &&
      plan.insuredPersonID.find(person => !isEmptyString(person.idNumber))
  );
  return (
    <PatientDetailsSection
      className="ehs-patient-details-insurance-plans"
      title={intl('view.patient.detail.insurancePlans')}
    >
      {isEmptyArray(insurancePlans) ? (
        <InsurancePlan
          insuranceCompanyName={getPatientDetailsNotSpecifiedString()}
          insuredPersonID={getPatientDetailsNotSpecifiedString()}
        />
      ) : (
        insurancePlans.map(plan => (
          <InsurancePlan
            key={plan.insurancePlanID}
            insuranceCompanyName={plan.insuranceCompanyName}
            insuredPersonID={
              // due to the filter that was applied previously, at least one insuredPersonID with a set idNumber should always exist
              // the fallback to the notSpecifiedString is only because Typescript needs it
              plan.insuredPersonID.find(
                person => !isEmptyString(person.idNumber)
              )?.idNumber ?? getPatientDetailsNotSpecifiedString()
            }
          />
        ))
      )}
    </PatientDetailsSection>
  );
}
